import styles from './Modal.module.scss';
import Textfield from '@atlaskit/textfield';
import Spinner from '@atlaskit/spinner';
import { TrelloPowerUpContext } from '@powerupsclub/trello';
import {
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import {
  clearAllRecentPolls,
  getRecentPolls,
} from '../../api/pollsInPluginData';
import { resizeIframe } from '../../api/resizeIframe';
import Button from '@atlaskit/button';

const host = window.location.host;
const isDevMode = host === 'localhost:3000' || host.includes('ngrok.io');

export interface RecentPollsListProps {
  onSelectPoll: (poll: any) => void;
}

export const RecentPollsList: React.FC<RecentPollsListProps> = ({
  onSelectPoll,
}) => {
  const { trelloIframe } = useContext(TrelloPowerUpContext);

  const [recentPolls, setRecentPolls] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  const filteredRecentPolls = useMemo(
    () =>
      recentPolls.filter((poll) =>
        poll.name.toLowerCase().includes(searchQuery.toLowerCase())
      ),
    [searchQuery, recentPolls]
  );

  useLayoutEffect(() => {
    resizeIframe(trelloIframe, '.AddPollModal');
  }, [trelloIframe, isLoading, recentPolls, searchQuery]);

  useEffect(() => {
    async function load() {
      const _recentPolls = await getRecentPolls(trelloIframe);

      setRecentPolls(_recentPolls);
      setIsLoading(false);
    }

    load();
  }, [trelloIframe]);

  if (isLoading) {
    return (
      <div className={styles.loadingSpinnerContainer}>
        <Spinner size="large" />
      </div>
    );
  }

  if (recentPolls.length === 0) {
    return (
      <div className={styles.noRecentPolls}>
        Recently created polls will appear here for easy access and reuse.
      </div>
    );
  }

  return (
    <div className={styles.recentPollsList}>
      {recentPolls.length > 10 && (
        <Textfield
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.currentTarget.value)}
          style={{ width: '100%' }}
          placeholder="Search for recent polls"
        />
      )}

      {filteredRecentPolls.length === 0 && (
        <div className={styles.noRecentPolls}>
          No polls found matching the search query.
        </div>
      )}

      <div className={styles.itemList}>
        {filteredRecentPolls.map((poll) => (
          <div
            className={styles.itemButton}
            key={poll.name}
            onClick={() => {
              onSelectPoll(poll);
            }}
          >
            {poll.name}
          </div>
        ))}
      </div>

      {isDevMode && (
        <Button
          appearance="danger"
          shouldFitContainer
          onClick={() => {
            clearAllRecentPolls(trelloIframe);
            setRecentPolls([]);
          }}
        >
          Clear all recent polls
        </Button>
      )}
    </div>
  );
};
